import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Mail, Download, CreditCard, FileText, Phone } from "lucide-react";
import { Seo } from "../components/Seo";

const FAQS = [
  {
    q: "Hur laddar jag ner min PDF?",
    a: "Direkt efter att du har betalat visas en stor blå knapp där det står \"Ladda ner din PDF\". Tryck på den så sparas filen på din enhet. Vi skickar även en kopia till din e-post.",
  },
  {
    q: "Hur öppnar jag PDF-filen?",
    a: "Tryck på den nedladdade filen. På en telefon eller surfplatta hittar du den oftast i appen \"Filer\". På en dator öppnas den direkt när du klickar på den längst ner i webbläsaren.",
  },
  {
    q: "Måste jag skapa ett konto?",
    a: "Nej. Du behöver bara skriva in din e-postadress i kassan. Inget lösenord och ingen registrering behövs.",
  },
  {
    q: "Hur betalar jag?",
    a: "Du betalar tryggt med bankkort i kassan. Betalningen sker via Stripe, en säker betaltjänst.",
  },
  {
    q: "Kan jag ångra mitt köp?",
    a: "Våra manualer och digitala kurser är digitala varor. När du har börjat ladda ner filen upphör ångerrätten, precis som lagen säger för digitala produkter. Du godkänner detta när du betalar. Är något oklart innan köp är du alltid välkommen att höra av dig först.",
  },
  {
    q: "Jag hittar inte min manual i e-posten. Vad gör jag?",
    a: "Titta först i mappen för skräppost. Om du fortfarande inte hittar den, skriv till oss på support@enklamanualer.se så hjälper vi dig direkt.",
  },
];

export default function Help() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Hjälp och vanliga frågor"
        description="Svar på vanliga frågor om nedladdning, betalning, PDF-filer och support. Personlig hjälp via e-post och livechatt – vi återkopplar samma dag."
        path="/hjalp"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] flex items-center gap-3">
        <HelpCircle size={44} strokeWidth={2.5} className="text-[#0056D2]" /> Hjälp
      </h1>
      <p className="text-lg text-[#4A4A4A] mt-4">
        Här hittar du svar på de vanligaste frågorna. Alla svar visas direkt – du behöver inte klicka.
      </p>

      <div className="space-y-5 mt-10">
        {FAQS.map((f, i) => (
          <div key={i} data-testid={`faq-${i}`} className="bg-white border-2 border-gray-300 rounded-xl p-6">
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A]">{f.q}</h2>
            <p className="text-lg leading-relaxed text-[#1A1A1A] mt-3">{f.a}</p>
          </div>
        ))}
      </div>

      <div data-testid="help-support-block" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-12">
        <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] flex items-center gap-3">
          <Phone size={28} strokeWidth={2.5} className="text-[#0056D2]" /> Personlig support när du behöver det
        </h2>
        <p className="text-lg leading-relaxed text-[#1A1A1A] mt-3">
          Vi vill att du ska känna dig helt trygg med ditt köp. Om du har frågor om en manual, eller
          behöver hjälp efter ditt köp, når du oss enkelt på support@enklamanualer.se. Vi har en fast
          svarsgaranti och återkopplar alltid till dig samma dag.
        </p>
        <p className="text-lg leading-relaxed text-[#1A1A1A] mt-4">
          Du kan även få hjälp direkt genom att använda vår livechatt. Du hittar den som en blå
          chattbubbla längst ner i det högra hörnet på din skärm, där du kan prata direkt med vår mänskliga support.
        </p>
      </div>

      <div className="bg-[#0056D2] text-white rounded-xl p-8 mt-6">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-3"><Mail size={28} /> Kontakta oss</h2>
        <p className="text-lg mt-3 text-blue-50">Vi svarar gärna på dina frågor. Skriv till oss så återkommer vi så snart vi kan.</p>
        <a href="mailto:support@enklamanualer.se" data-testid="help-email" className="inline-flex items-center gap-2 text-xl font-bold mt-4 underline">
          support@enklamanualer.se
        </a>
      </div>

      <div className="text-center mt-10">
        <Link to="/" data-testid="help-back-home" className="text-lg font-semibold text-[#0056D2] hover:underline">Tillbaka till start</Link>
      </div>
    </div>
  );
}
